import React from "react";
import {
  compose,
  withPropsOnChange,
  withStateHandlers,
  withHandlers,
} from "recompose";
import {
  Table,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import { Page, onMount, FormGroup } from "components/common";
import { formatDate, formatCurrency, formatNumber, CurrencySymbol } from "services/util";
import { keyBy, isUndefined, startsWith } from "lodash";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { withDocumentRenderer, Attachments } from "components";
import { ReceiveTransactionReceipt } from "components/documents";
import GenericLinesDocument from "components/documents/GenericLinesDocument";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import { TrackingSelect } from "../Settings/ManualJournals/ManualJournalsView";
import { useQuery } from "react-query";
import { NotificationManager } from "react-notifications";
import BackButton from "components/refactoring/BackButton";

const TH = ({ children, ...props }) => (
  <th {...props}>
    <Trans>{children}</Trans>
  </th>
);

const lineAmountTypesMap = {
  Inclusive: "Tax Inclusive",
  Exclusive: "Tax Exclusive",
  NoTax: "No Tax",
};

const TextFormGroup = ({ labelProps, children, ...props }) => (
  <FormGroup
    {...props}
    className="mb-0 mr-3"
    labelProps={{ className: "font-weight-bold", ...labelProps }}
  >
    <div className="form-control-plaintext" style={{color: children==="DELETED" ? 'red':'-moz-initial'}}>{children}</div>
  </FormGroup>
);

export const Transaction = ({
  state,
  xero,
  accounts,
  title,
  contactLabel,
  taxRates,
  document,
  DownloadDocument,
  EmailDocument,
  DocumentRenderer,
  documentType,
  handleAttachmentsChanged,
  history,
  t,
  ...props
}) => {

  const { data: trackingCategories } = useQuery(
    [
      "trackingcategories",
      {
        params: {
          Status: "ACTIVE",
        },
      },
    ],
    ({ queryKey }) => xero.get(...queryKey).get("data"),
    {
      placeholderData: [],
    }
  );
  
  const onRemoveAndRedo = () => {
    xero.put(`banktransactions/${state.BankTransactionID}`, {
      BankTransactionID: state.BankTransactionID,
      Status: "DELETED",
    })
      .then(() => {
        NotificationManager.success(t("The account transaction has been removed"));
        history.push(`/bank/bank_accounts/${state.BankAccount.AccountID}/bank_transactions?removeRedo=True&Type=${state.Type}`);
      })
      .catch((error) => {
        NotificationManager.error(t("An error Occured."));
        console.error("Error:", error);
      });
  };

  const transaction_type = startsWith(state.Type, 'RECEIVE') ? 'receive' : 'spend'
  const isTransfer = state?.Type === "SPEND-TRANSFER" || state?.Type === "RECEIVE-TRANSFER"
  const isOverpayment = state?.Type === "SPEND-OVERPAYMENT" || state?.Type === "RECEIVE-OVERPAYMENT"

return (

  <Page title={title}>
    <BackButton/>
    {state.Status !== "DELETED" && 
    <div className="d-flex justify-content-end">
      {!!(state.BankTransactionID && state.Attachments) && (
        <Attachments
          xero={xero}
          hideIncludeOnline
          onAttachmentsChanged={handleAttachmentsChanged}
          baseUrl={`banktransactions/${state.BankTransactionID}`}
          attachments={state.Attachments}
        />
      )}
      {!!document && (
        <div>
          <DownloadDocument className="mx-2" />
          <EmailDocument item={state} type={documentType} />
          <DocumentRenderer component={document} />
        </div>
      )}
      {!state.IsReconciled && (startsWith(state.Type, "SPEND") || startsWith(state.Type, "RECEIVE")) ? (
        <UncontrolledButtonDropdown className="ml-2">
          <DropdownToggle caret color="primary" className="btn-sm">
            <Trans>Options</Trans>
          </DropdownToggle>
          <DropdownMenu>
            {startsWith(state.Type, "SPEND") && (
              <Link className="dropdown-item" to={`/bank/bank_accounts/${state.BankAccount.AccountID}/bank_transactions/spend/${state.BankTransactionID}`}>
                Edit Transaction
              </Link>
            )}
            {startsWith(state.Type, "RECEIVE") && (
              <Link className="dropdown-item" to={`/bank/bank_accounts/${state.BankAccount.AccountID}/bank_transactions/receive/${state.BankTransactionID}`}>
                Edit Transaction
              </Link>
            )}
            {state.OverpaymentID && (
              <Link className="dropdown-item" to={`/transactions/${transaction_type}/overpayments/${state.OverpaymentID}`}>
                <Trans>View Overpayment</Trans>
              </Link>
            )}
            {state.PrepaymentID && (
              <Link className="dropdown-item" to={`/transactions/${transaction_type}/prepayments/${state.PrepaymentID}`}>
                <Trans>View Prepayment</Trans>
              </Link>
            )}
              <Link className="dropdown-item" onClick={() => onRemoveAndRedo()}>
                <Trans>Remove & Redo</Trans>
              </Link>
          </DropdownMenu>
        </UncontrolledButtonDropdown>
      ) : null}
    </div>}
    <div className="d-flex">
    {isTransfer ? 
      <>
        <TextFormGroup label="Transfer Date">{formatDate(state.Date)}</TextFormGroup>
        {state.Reference && <TextFormGroup label="Reference">{state?.Reference}</TextFormGroup>}
      </>
      :
      <>
        <TextFormGroup label={contactLabel}>
          <Link to={`/contacts/${state.Contact.ContactID}`}>{state.Contact.Name}</Link>
        </TextFormGroup>
        <TextFormGroup label="Date">{formatDate(state.Date)}</TextFormGroup>
        {state.Reference && <TextFormGroup label="Reference">{state?.Reference}</TextFormGroup>}
        <TextFormGroup label="Total">{formatCurrency(state.Total)}</TextFormGroup>
        <TextFormGroup label="Status">{state.Status}</TextFormGroup>
        {state.OverpaymentID && <TextFormGroup label="Details"><Link to={`/transactions/${transaction_type}/overpayments/${state.OverpaymentID}`}><Trans>Overpayment Details</Trans></Link></TextFormGroup>}
        {state.PrepaymentID && <TextFormGroup label="Details"><Link to={`/transactions/${transaction_type}/prepayments/${state.PrepaymentID}`}><Trans>Prepayment Details</Trans></Link></TextFormGroup>}
      </> 
    }
    </div>
    <hr />
  {!isTransfer ? 
    <>
    <div className="d-flex justify-content-end mb-2">
      <div>
        <Trans>Amounts are</Trans>{" "}
        <strong>
          <Trans>
            {lineAmountTypesMap[state.LineAmountTypes] || state.LineAmountTypes}
          </Trans>
        </strong>
      </div>
    </div>
    <Table size="sm">
      <thead>
        <tr>
          {!isOverpayment && <TH>Item Code</TH>}
          <TH>Description</TH>
          <TH>Quantity</TH>
          <TH>Unit Price</TH>
          <TH>Account</TH>
          <TH>Tax Rate</TH>
          {trackingCategories?.map((i) => (
            <TH key={i.TrackingCategoryID}>{i.Name}</TH>
          ))}
          <TH>Amount</TH> <CurrencySymbol currency={state.CurrencyCode} />
        </tr>
      </thead>
      <tbody>
        {state.LineItems.map((i, idx) => (
          <tr key={idx}>
            {!isOverpayment && <td>{i.ItemCode}</td>}
            <td>{i.Description}</td>
            <td>{formatNumber(i.UnitAmount? i.LineAmount/i.UnitAmount: 0)}</td>
            <td>{formatCurrency(i.UnitAmount)}</td>
            <td>{(accounts[i.AccountCode] || {}).Name}</td>
            <td>{(taxRates[i.TaxType] || {}).Name}</td>
            {trackingCategories?.map((j) => (
              <td key={j.TrackingCategoryID}>
                <TrackingSelect
                  lineItem={i}
                  trackingCategory={j}
                  lineItemIndex={idx}
                  disabled
                />
              </td>
            ))}
            <td>{formatCurrency(i.LineAmount)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
    <div className="d-flex justify-content-end mb-2">
      <span className="mr-5">
        <Trans>Subtotal</Trans>
      </span>
      {formatCurrency(state.SubTotal)}
    </div>
    {!isUndefined(state.TotalTax) && (
      <div className="d-flex justify-content-end mb-2">
        <span className="mr-5">
          <Trans>Total Tax</Trans>
        </span>
        {formatCurrency(state.TotalTax)}
      </div>
    )}
    </> 
    : 
    <>
      <Table size="sm" className="border">
        <thead>
          <tr>
            <TH className="text-left">Transferred {state.Type === "SPEND-TRANSFER" ? "to" : "from"}</TH>     
            <TH className="text-right">Amount</TH> <CurrencySymbol currency={state.CurrencyCode} />
          </tr>
        </thead>
        <tbody>
          {state.LineItems.map((i, idx) => (
            <tr key={idx}>
              <td className="text-left">{i.Description}</td>
              <td className="text-right">{(i.UnitAmount < 0) ? `(${formatCurrency(Math.abs(i.UnitAmount))})` : formatCurrency(i.UnitAmount)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
    }
    <div className="d-flex justify-content-end">
      <div className="border-dark border-top border-bottom p-2 h3 d-flex">
        <span className="mr-5">
          <Trans>Total</Trans>
        </span>{" "}
        <span>{formatCurrency(state.Total)}</span>
      </div>
    </div>
  </Page>
)};

const titles = {
  SPEND: "Spend Money",
  RECEIVE: "Receive Money",
  "SPEND-OVERPAYMENT": "Overpayment",
  "SPEND-PREPAYMENT": "Prepayment",
  "RECEIVE-OVERPAYMENT": "Overpayment",
  "RECEIVE-PREPAYMENT": "Prepayment",
  "SPEND-TRANSFER" : "Bank Transfer (Spend)",
  "RECEIVE-TRANSFER" : "Bank Transfer (Receive)",
};

export const enhance = compose(
  withOrganisationContextConsumer,
  onMount(
    ({ xero, match, fetchOrganisation }) =>
      () =>
        Promise.props({
          state: xero.get(`banktransactions/${match.params.id}`).get("data"),
          organisation: fetchOrganisation(),
          accounts: xero
            .get("accounts")
            .get("data")
            .then((d) => keyBy(d, "Code")),
          taxRates: xero
            .get("taxrates")
            .get("data")
            .then((d) => keyBy(d, "TaxType")),
        })
  ),
  withStateHandlers(({ state }) => ({ state }), {
    setAttachments:
      ({ state }) =>
      (Attachments) => ({
        ...state,
        Attachments,
      }),
  }),
  withPropsOnChange(["state"], ({ state }) => {
    if (state.Type === "RECEIVE") {
      return {
        document: ReceiveTransactionReceipt,
        documentType: "RECEIPT",
      };
    } else if (
      startsWith(state.Type, "SPEND") ||
      startsWith(state.Type, "RECEIVE")
    ) {
      return {
        document: GenericLinesDocument,
        documentType: "GENERIC_LINES",
      };
    }
  }),
  withDocumentRenderer({
    onDownload:
      ({ state, xero, documentType, organisation }) =>
      (getDocument) => {
        getDocument(
          { item: state },
          `${documentType} from ${organisation.Name}.pdf`
        );
      },
  }),
  withHandlers({
    handleAttachmentsChanged:
      ({ xero, match, dispatch, setAttachments }) =>
      () => {
        xero
          .get(`banktransactions/${match.params.id}/attachments`)
          .get("data")
          .tap((d) => setAttachments(d));
      },
  }),
  withPropsOnChange(["state"], ({ state }) => ({
    title: `Transaction: ${titles[state.Type] || ""}`,
    contactLabel: startsWith(state.Type, "SPEND") ? "To" : "From",
  }))
);

export default enhance(Transaction);
